import type { MetricsClient } from '../types/MetricsClient'
import { getCurrentSite } from '@strata-monorepo/utils/_SiteConfig'
import { MetricsLogger } from '../utils/MetricsLogger'
import { createDeduplicationKey } from '../utils/MetricsDeduplication'
import { getRvUserType } from '@strata-monorepo/utils/rvUserType'
import { getMetricsAudience } from '../utils/audienceContext'

export interface NewRelicBrowser {
  addPageAction: (name: string, attributes?: Record<string, unknown>) => void
  noticeError: (error: Error, attributes?: Record<string, unknown>) => void
  recordCustomEvent: (eventType: string, attributes: Record<string, unknown>) => void
  setCustomAttribute: (name: string, value: string | number | boolean) => void
}

export class NewRelicBrowserAdapter implements MetricsClient {
  private client: NewRelicBrowser
  private metricsLogger = MetricsLogger.getInstance()
  private userType = getRvUserType()

  constructor(nrImplementation: NewRelicBrowser) {
    this.client = nrImplementation

    // Set user type as New Relic custom attribute on initialization
    try {
      this.client.setCustomAttribute('userType', this.userType)
    } catch (error) {
      console.log('Failed to set userType custom attribute:', error)
    }
  }

  /**
   * Get permanent tags that should be included in all metrics.
   * @returns Record with domain, environment, userType, and audience tags.
   */
  private getPermanentTags(): Record<string, string> {
    const domain = getCurrentSite({ useNewSite: false })
    const environment = import.meta.env['VITE_ENVIRONMENT'] || 'development'
    const audience = getMetricsAudience()

    return {
      domain,
      environment,
      userType: this.userType,
      audience,
    }
  }

  increment(name: string, value: number, tags?: Record<string, string>): void {
    const deduplicationKey = createDeduplicationKey(name, tags)

    if (!this.metricsLogger.shouldLogMetric(deduplicationKey)) {
      return // Skip duplicate
    }

    try {
      const permanentTags = this.getPermanentTags()

      this.client.addPageAction(name, {
        value,
        type: 'increment',
        ...tags,
        ...permanentTags,
      })
    } catch (error) {
      // TODO: conditional logging
      console.log(error)
    }
  }

  gauge(name: string, value: number, tags?: Record<string, string>): void {
    const deduplicationKey = createDeduplicationKey(name, tags)

    if (!this.metricsLogger.shouldLogMetric(deduplicationKey)) {
      return // Skip duplicate
    }

    try {
      const permanentTags = this.getPermanentTags()

      this.client.addPageAction(name, {
        value,
        type: 'gauge',
        ...tags,
        ...permanentTags,
      })
    } catch (error) {
      // TODO: conditional logging
      console.log(error)
    }
  }

  trackEvent(name: string, attributes: Record<string, string | number | boolean>): void {
    try {
      const permanentTags = this.getPermanentTags()

      this.client.addPageAction(name, { ...attributes, ...permanentTags })
    } catch (error) {
      // TODO: conditional logging
      console.log(error)
    }
  }
}
